import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
// Using simple HTML icons instead of react-icons for TypeScript compatibility
import {
  calculateDetailedRoi,
  DetailedRoiInput,
  DetailedRoiResult,
} from "../services/detailedRoiCalculator";
import NetSavingsBarChart from "../components/NetSavingsBarChart";
import ReportModal from "../components/roi/ReportModal";
import MetaTags from "../components/SEO/MetaTags";
import { pageConfigs } from "../utils/seo";

interface FormValues {
  annualAudits: string;
  auditPrepHours: string;
  auditStaffSalary: string;
  monthlyClaims: string;
  denialRate: string;
  deniedClaimValue: string;
  appealSuccessRate: string;
}

const DetailedRoiCalculator = () => {
  const location = useLocation();
  const [formValues, setFormValues] = useState<FormValues>({
    annualAudits: "",
    auditPrepHours: "",
    auditStaffSalary: "",
    monthlyClaims: "",
    denialRate: "",
    deniedClaimValue: "",
    appealSuccessRate: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [calculatedResults, setCalculatedResults] =
    useState<DetailedRoiResult | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  
  // Preserve data from quick calculator
  const [quickCalculatorData, setQuickCalculatorData] = useState<any>(null);

  useEffect(() => {
    // Check if we have data from the quick calculator
    if (location.state?.quickCalculatorResults) {
      setQuickCalculatorData(location.state.quickCalculatorResults);
    }
  }, [location.state]);

  const handleFormChange = (field: keyof FormValues, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formValues.annualAudits ||
      !formValues.auditPrepHours ||
      !formValues.auditStaffSalary ||
      !formValues.monthlyClaims ||
      !formValues.denialRate ||
      !formValues.deniedClaimValue ||
      !formValues.appealSuccessRate
    ) {
      return;
    }

    const input: DetailedRoiInput = {
      annualAudits: parseInt(formValues.annualAudits),
      auditPrepHours: parseInt(formValues.auditPrepHours),
      auditStaffSalary: parseInt(formValues.auditStaffSalary),
      monthlyClaims: parseInt(formValues.monthlyClaims),
      denialRate: parseInt(formValues.denialRate),
      deniedClaimValue: parseInt(formValues.deniedClaimValue),
      appealSuccessRate: parseInt(formValues.appealSuccessRate),
    };
    const results = calculateDetailedRoi(input);
    setCalculatedResults(results);
    setIsSubmitted(true);
  };

  return (
    <>
      <MetaTags
        title={pageConfigs.detailedRoiCalculator.title}
        description={pageConfigs.detailedRoiCalculator.description}
        keywords={pageConfigs.detailedRoiCalculator.keywords}
        canonical={pageConfigs.detailedRoiCalculator.canonical}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#1e3d3b]/20 to-[#1e3d3b]/40"></div>
        <div className="relative px-6 py-24 md:py-32 lg:py-40">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight font-productSans">
              Clinic Financial Impact Calculator
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
              Use <span className="font-bold text-[#619dd2]">Notation</span> to analyze the hidden costs of audits, claim denials, documentation prep, and appeal outcomes. Gain a clear view of your clinic's financial exposure and uncover key opportunities for optimization and recovery.
            </p>
          </div>
        </div>
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
      </section>

      <section className="py-20 px-6">
        <div className="mx-auto max-w-7xl">
          <div className="flex justify-start mb-12">
        <Link
          to="/roi-calculator"
              state={{ preserveData: true, quickCalculatorData }}
              className="bg-[#1e3d3b] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#2a5a56] transition-colors flex items-center gap-2"
        >
              <span>←</span> Back to Quick ROI Calculator
        </Link>
      </div>
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="w-full lg:w-2/5">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">
                  Get Your Clinic's Financial Estimates
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="annualAudits"
                      className="block text-sm font-medium text-slate-700 mb-1"
              >
                Annual Audits
              </label>
              <select
                id="annualAudits"
                name="annualAudits"
                value={formValues.annualAudits}
                onChange={(e) =>
                  handleFormChange("annualAudits", e.target.value)
                }
                      className="mt-1 block w-full border border-slate-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
              >
                      <option value="" disabled>
                        How many audits happen in the clinic annually?
                      </option>
                <option value="4">4 audits</option>
                <option value="6">6 audits</option>
                <option value="8">8 audits</option>
                <option value="12">12 audits</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="auditPrepHours"
                      className="block text-sm font-medium text-slate-700 mb-1"
              >
                Audit Preparation Hours
              </label>
              <select
                id="auditPrepHours"
                name="auditPrepHours"
                value={formValues.auditPrepHours}
                onChange={(e) =>
                  handleFormChange("auditPrepHours", e.target.value)
                }
                      className="mt-1 block w-full border border-slate-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
              >
                      <option value="" disabled>
                        How many hours are spent in preparing audit
                        documentation?
                      </option>
                <option value="20">20 hours</option>
                <option value="30">30 hours</option>
                <option value="40">40 hours</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="auditStaffSalary"
                      className="block text-sm font-medium text-slate-700 mb-1"
              >
                Audit Staff Hourly Salary
              </label>
              <select
                id="auditStaffSalary"
                name="auditStaffSalary"
                value={formValues.auditStaffSalary}
                onChange={(e) =>
                  handleFormChange("auditStaffSalary", e.target.value)
                }
                      className="mt-1 block w-full border border-slate-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
              >
                      <option value="" disabled>
                        What is the average audit staff salary?
                      </option>
                <option value="30">$30/hour</option>
                <option value="40">$40/hour</option>
                <option value="50">$50/hour</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="monthlyClaims"
                      className="block text-sm font-medium text-slate-700 mb-1"
              >
                Monthly Claims Submitted
              </label>
              <select
                id="monthlyClaims"
                name="monthlyClaims"
                value={formValues.monthlyClaims}
                onChange={(e) =>
                  handleFormChange("monthlyClaims", e.target.value)
                }
                      className="mt-1 block w-full border border-slate-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
              >
                      <option value="" disabled>
                        How many claims are processed each month?
                      </option>
                <option value="1000">1,000 claims</option>
                <option value="2000">2,000 claims</option>
                <option value="5000">5,000 claims</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="denialRate"
                      className="block text-sm font-medium text-slate-700 mb-1"
              >
                Denial Rate (%)
              </label>
              <select
                id="denialRate"
                name="denialRate"
                value={formValues.denialRate}
                      onChange={(e) =>
                        handleFormChange("denialRate", e.target.value)
                      }
                      className="mt-1 block w-full border border-slate-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
              >
                      <option value="" disabled>
                        What is the audit denial rate?
                      </option>
                <option value="10">10%</option>
                <option value="15">15%</option>
                <option value="20">20%</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="deniedClaimValue"
                      className="block text-sm font-medium text-slate-700 mb-1"
              >
                Average Denied Claim Value ($)
              </label>
              <select
                id="deniedClaimValue"
                name="deniedClaimValue"
                value={formValues.deniedClaimValue}
                onChange={(e) =>
                  handleFormChange("deniedClaimValue", e.target.value)
                }
                      className="mt-1 block w-full border border-slate-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
              >
                      <option value="" disabled>
                        What is the average value of denied claim?
                      </option>
                <option value="10">$10</option>
                <option value="100">$100</option>
                <option value="500">$500</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="appealSuccessRate"
                      className="block text-sm font-medium text-slate-700 mb-1"
              >
                Appeal Success Rate (%)
              </label>
              <select
                id="appealSuccessRate"
                name="appealSuccessRate"
                value={formValues.appealSuccessRate}
                onChange={(e) =>
                  handleFormChange("appealSuccessRate", e.target.value)
                }
                      className="mt-1 block w-full border border-slate-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
              >
                      <option value="" disabled>
                        What is the appeal success rate?
                      </option>
                <option value="0">0%</option>
                <option value="25">25%</option>
                <option value="50">50%</option>
              </select>
            </div>
                  <button
                    type="submit"
                    className="w-full bg-[#1e3d3b] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#2a5a56] transition-colors"
                  >
              Calculate
            </button>
          </form>
              </div>
        </div>

            <div className="w-full lg:w-3/5">
              {!isSubmitted ? (
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-8 shadow-lg h-full flex flex-col justify-center items-center">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4 text-center">
                    Your Detailed Financial Analysis
                  </h3>
                  <p className="text-slate-600 text-center">
                    Fill out the form to see a detailed breakdown of your
                    potential savings.
                  </p>
                </div>
              ) : (
                calculatedResults && (
                  <div className="space-y-8">
                    {/* Net Savings Summary */}
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-8 shadow-lg">
                      <div className="bg-slate-50 p-6 rounded-lg mb-8">
                        <h2 className="text-xl font-bold text-slate-800 mb-2">
                  Net Savings with Fownd
                </h2>
                        <p className="text-lg text-slate-600">
                  By implementing Fownd, your practice can save up to{" "}
                          <span className="font-bold text-emerald-600">
                    ${calculatedResults.netSavings.toLocaleString()}
                  </span>{" "}
                          annually.
                </p>
              </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-slate-50 p-4 rounded-lg flex flex-col items-center justify-center text-center">
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">
                    Staff Cost Savings
                  </h3>
                          <p className="text-xs text-slate-500 mb-3">
                            Reduction in audit preparation hours
                  </p>
                  <NetSavingsBarChart
                            colors={["#475569", "#1e3d3b"]}
                            legendText="Lower is better"
                    data={[
                      {
                        label: "Without Fownd",
                                value:
                                  calculatedResults.annualAuditCost.withoutFownd,
                      },
                      {
                        label: "With Fownd",
                        value: calculatedResults.annualAuditCost.withFownd,
                      },
                    ]}
                  />
                </div>
                        <div className="bg-slate-50 p-4 rounded-lg flex flex-col items-center justify-center text-center">
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">
                            Denial Management
                  </h3>
                          <p className="text-xs text-slate-500 mb-3">
                            Reduction in revenue lost to denials
                  </p>
                  <NetSavingsBarChart
                            colors={["#475569", "#1e3d3b"]}
                            legendText="Lower is better"
                    data={[
                      {
                        label: "Without Fownd",
                                value:
                                  calculatedResults.annualRevenueLost.withoutFownd,
                      },
                      {
                        label: "With Fownd",
                                value:
                                  calculatedResults.annualRevenueLost.withFownd,
                      },
                    ]}
                  />
                </div>
                        <div className="bg-slate-50 p-4 rounded-lg flex flex-col items-center justify-center text-center">
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">
                    Revenue Recovery
                  </h3>
                          <p className="text-xs text-slate-500 mb-3">
                            Improved success in appealing denials
                  </p>
                  <NetSavingsBarChart
                            colors={["#475569", "#1e3d3b"]}
                            legendText="Higher is better"
                    data={[
                      {
                        label: "Without Fownd",
                                value:
                                  calculatedResults.recoveredRevenue.withoutFownd,
                      },
                      {
                        label: "With Fownd",
                                value:
                                  calculatedResults.recoveredRevenue.withFownd,
                      },
                    ]}
                  />
                </div>
              </div>

                      {/* Download Comprehensive Report Button */}
                      <div className="text-center mt-8">
                        <button
                          onClick={() => setShowReportModal(true)}
                          className="bg-[#1e3d3b] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#2a5a56] transition-colors flex items-center justify-center mx-auto gap-2"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          Download Comprehensive ROI Report for your Clinic
                        </button>
                      </div>
                    </div>
                  </div>
                )
          )}
        </div>
      </div>
        </div>
      </section>

      {/* Report Modal */}
      {showReportModal && calculatedResults && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          results={{
            // Use quick calculator data if available, otherwise provide defaults
            metrics: quickCalculatorData?.results?.metrics || {
              revenueGain: calculatedResults.netSavings * 1.2,
              hoursSaved: 264,
              patientRetention: 85,
              documentationAccuracy: 95,
              costSavings: calculatedResults.netSavings,
            },
            perTherapist: quickCalculatorData?.results?.perTherapist || {
              revenueGain: 66000,
              hoursSaved: 132,
              costSavings: calculatedResults.netSavings / 2,
            },
            // Always add the detailed calculations from the current page
            detailedCalculations: {
              netSavings: calculatedResults.netSavings,
              staffCostSavings: calculatedResults.annualAuditCost.withoutFownd - calculatedResults.annualAuditCost.withFownd,
              denialManagement: calculatedResults.annualRevenueLost.withoutFownd - calculatedResults.annualRevenueLost.withFownd,
              revenueRecovery: calculatedResults.recoveredRevenue.withFownd - calculatedResults.recoveredRevenue.withoutFownd,
            },
          }}
          formData={quickCalculatorData?.formData || null}
          detailedFormData={formValues}
          isDetailed={true}
        />
      )}
      </div>
    </>
  );
};

export default DetailedRoiCalculator;
